import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { BlockType, BlockWithNotes } from '../../../../config/api/models/Block';
import { Exercise, ExerciseLevel } from '../../../../config/api/models/Exercise';
import { GroupStudentSeriesScores } from '../../../../config/api/models/Score';
import { ListItemActionButton } from '../../../shared/components';
import { getColorByLevel, getDifficultyByLevel } from '../../helpers';
import TeacherExerciseTooltip from '../SeriesDetail/ExerciseList/TeacherExerciseTooltip';
import { ExerciseTitleBarScoreInfo } from './ExerciseTitleBarScoreInfo';
import { ExerciseTitleBarEditActions } from './ExerciseTitleBarEditActions';
import { UserBlockAnswers } from '../../../../config/api/models/Answers';
import { EMPTY_SCORE_TYPES, QuestionType } from '../../../../config/api/models/QuestionType';
import { useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';
import { InfoWrapper, Wrapper, IconWrapper } from './Atoms';
import ExerciseTitleBarHeader from './ExerciseTitleBarHeader';
import RefreshButton from './RefreshButton';
import { SpriteIcon, SpriteIconsLight } from 'src/config/api/models/FontAwesome';
import { useParams } from 'react-router-dom';
import { UrlParams } from 'src/modules/shared/types';

type Props = {
  exercise: Exercise | BlockWithNotes;
  seriesId: string;
  userAnswersFromPage?: UserBlockAnswers;
  groupAnswersFromPage?: UserBlockAnswers[];
  teacherProgressAndScores?: GroupStudentSeriesScores;
  averageExerciseScoreForGroup?: string;
  mode: 'read' | 'edit';
  index?: number;
  onDeleteExercise?: () => void;
  onUpdateExercise?: () => void;
  onDuplicateExercise?: () => void;
  hideDeleteButton?: boolean;
  hideTitlePrefix?: boolean;
  feedbackEnabled?: boolean;
  onToggleFeedback: (event: React.MouseEvent<HTMLButtonElement>) => void;
  expanded: string | boolean;
};

export const ExerciseTitleBar: FC<Props> = ({
  exercise,
  seriesId,
  userAnswersFromPage,
  groupAnswersFromPage,
  teacherProgressAndScores,
  averageExerciseScoreForGroup,
  mode,
  index,
  onDeleteExercise,
  onUpdateExercise,
  onDuplicateExercise,
  hideDeleteButton = false,
  hideTitlePrefix = false,
  expanded,
}) => {
  const { t } = useTranslation();
  const selectedGroup = useAppSelector((state) => state.filters.selectedGroup);
  const selectedStudent = useAppSelector((state) => state.filters.selectedStudent);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  let params = useParams<UrlParams>();

  const userLastScore = useMemo(() => {
    const blockAsExercise = exercise as Exercise;

    if (selectedStudent && !userAnswersFromPage) return '';
    if (!userAnswersFromPage) return undefined;
    if (!userAnswersFromPage.attempts) return undefined;
    if (
      blockAsExercise.question_type != null &&
      EMPTY_SCORE_TYPES.includes(blockAsExercise.question_type as QuestionType)
    )
      return '';
    if (userAnswersFromPage?.attempts?.last?.maxScore === 0) return '';
    return `${userAnswersFromPage?.attempts?.last?.score}/${userAnswersFromPage?.attempts?.last?.maxScore}`;
  }, [userAnswersFromPage, exercise, selectedStudent]);

  const groupAverageScore = useMemo(() => {
    const blockAsExercise = exercise as Exercise;
    if (!groupAnswersFromPage) return undefined;
    if (groupAnswersFromPage.length === 0) return undefined;
    if (!groupAnswersFromPage[0].attempts) return undefined;
    if (
      blockAsExercise.question_type != null &&
      EMPTY_SCORE_TYPES.includes(blockAsExercise.question_type as QuestionType)
    )
      return '';

    let aggregateScore = 0;
    let aggregateMaxScore = 0;

    for (let i = 0; i < groupAnswersFromPage.length; i++) {
      aggregateScore += groupAnswersFromPage[i].attempts?.last?.score || 0;
      aggregateMaxScore += groupAnswersFromPage[i].attempts?.last?.maxScore || 0;
    }

    if (aggregateMaxScore === 0) return '';

    return `${Math.round((aggregateScore / aggregateMaxScore) * 100)}%`;
  }, [exercise, groupAnswersFromPage]);

  const getExerciseToolInfo = () => {
    const tooltipLevelText = t(
      `trajectory.sections.shared.level.${getDifficultyByLevel(
        exercise.level?.toString() as ExerciseLevel
      )}`
    );
    let exerciseToolInfo: { icon: SpriteIcon; message: string | null } = {
      icon: SpriteIconsLight.LIST,
      message: null,
    };
    switch (exercise.exercise_tool) {
      case 'fully_digital':
        exerciseToolInfo = {
          icon: SpriteIconsLight.BROWSER,
          message: 'Volledig digitaal',
        };
        break;
      case 'digital_with_paper':
        exerciseToolInfo = {
          icon: SpriteIconsLight.FILE_PEN,
          message: 'Digitaal met papier',
        };
        break;
      default:
        break;
    }
    const finalExerciseToolInfo = {
      ...exerciseToolInfo,
      message: (
        <>
          <p>{tooltipLevelText}</p>
          {exercise.exercise_tool !== null && <p>{exerciseToolInfo.message}</p>}
        </>
      ),
    };
    return finalExerciseToolInfo;
  };

  const exerciseToolInfo = getExerciseToolInfo();

  return (
    <>
      <Wrapper page={params.sectionCode} expanded={expanded}>
        <ExerciseTitleBarHeader
          isMobile={isMobile}
          exercise={exercise}
          index={index}
          hideTitlePrefix={hideTitlePrefix}
          userLastScore={userLastScore}
          groupAverageScore={groupAverageScore}
          expanded={expanded}
        />
        <InfoWrapper>
          {!isMobile && (
            <ExerciseTitleBarScoreInfo
              userLastScore={userLastScore}
              groupAverageScore={groupAverageScore}
            />
          )}
          {exercise.type === BlockType.ALGEBRA_KIT &&
            mode === 'read' &&
            exercise.algebra_kit_exercise_id && (
              <RefreshButton algebrakitExerciseId={exercise.algebra_kit_exercise_id} />
            )}
          <ExerciseTitleBarEditActions
            exercise={exercise as Exercise}
            mode={mode}
            onDeleteExercise={onDeleteExercise}
            onDuplicateExercise={onDuplicateExercise}
            onUpdateExercise={onUpdateExercise}
            hideDeleteButton={hideDeleteButton}
          />
          <IconWrapper>
            {!!exercise.level && (
              <ListItemActionButton
                icon={exerciseToolInfo.icon}
                colorVariant={getColorByLevel(exercise.level, theme)}
                tooltip={
                  !!teacherProgressAndScores &&
                  selectedGroup !== '' &&
                  averageExerciseScoreForGroup !== '' ? (
                    <TeacherExerciseTooltip
                      data={teacherProgressAndScores.students}
                      seriesId={seriesId}
                      exerciseId={exercise.id}
                    />
                  ) : (
                    exerciseToolInfo.message
                  )
                }
              />
            )}
          </IconWrapper>
        </InfoWrapper>
      </Wrapper>
    </>
  );
};
